import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Plus, Trash2, FileSpreadsheet, Megaphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCampaignLists, useDeleteCampaignList, type CampaignList } from '../api/campaigns'
import { CampaignUploadModal } from '../components/campaigns/CampaignUploadModal'
import { formatDateTime } from '../lib/utils'
import { cn } from '../lib/utils'

export function CampaignListsPage() {
  const { t } = useTranslation('campaigns')
  const [uploadOpen, setUploadOpen] = useState(false)
  const { data: lists, isLoading, refetch } = useCampaignLists()
  const deleteMutation = useDeleteCampaignList()
  const navigate = useNavigate()

  const handleDelete = (e: React.MouseEvent, list: CampaignList) => {
    e.stopPropagation()
    if (confirm(t('list.deleteConfirm', { name: list.name, count: list.total_contacts }))) {
      deleteMutation.mutate(list.id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {t('list.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t('list.uploadList')}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Empty state */}
      {lists && lists.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border py-16">
          <Megaphone className="h-12 w-12 text-text-muted" />
          <h3 className="mt-4 text-lg font-semibold text-text-primary">{t('list.emptyTitle')}</h3>
          <p className="mt-2 text-sm text-text-secondary">
            {t('list.emptySubtitle')}
          </p>
          <button
            onClick={() => setUploadOpen(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t('list.uploadList')}
          </button>
        </div>
      )}

      {/* Campaign list cards */}
      {lists && lists.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lists.map((list) => {
            const pct = list.stats.total > 0
              ? Math.round((list.stats.contacted / list.stats.total) * 100)
              : 0
            const isComplete = list.stats.remaining === 0 && list.stats.total > 0

            return (
              <div
                key={list.id}
                onClick={() => navigate(`/app/campaigns/${list.id}`)}
                className="group cursor-pointer rounded-lg border border-border bg-surface-card p-5 transition-all hover:border-primary/50 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-lg',
                      isComplete ? 'bg-success/10' : 'bg-primary/10'
                    )}>
                      <FileSpreadsheet className={cn(
                        'h-5 w-5',
                        isComplete ? 'text-success' : 'text-primary'
                      )} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-text-primary group-hover:text-primary">
                        {list.name}
                      </h3>
                      <p className="text-xs text-text-muted">{list.file_name}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, list)}
                    className="rounded-lg p-1.5 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover:opacity-100"
                    title={t('list.deleteCampaign')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-elevated">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        isComplete ? 'bg-success' : 'bg-primary'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
                    <span>{t('list.calledOf', { contacted: list.stats.contacted, total: list.stats.total })}</span>
                    <span>
                      {isComplete ? (
                        <span className="font-medium text-success">{t('list.complete')}</span>
                      ) : (
                        t('list.remaining', { count: list.stats.remaining })
                      )}
                    </span>
                  </div>
                </div>

                <p className="mt-3 text-xs text-text-muted">
                  {t('list.createdOn', { date: formatDateTime(list.created_at) })}
                </p>
              </div>
            )
          })}
        </div>
      )}

      {/* Upload modal */}
      <CampaignUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  )
}
